/**
 * Basis Tracking Pixel Block
 * 
 * Reference: https://github.com/centro/basis-google-tag-manager/blob/main/README.md
 *
 * Loads the Basis Universal Pixel script and fires the universal page-view call.
 */

import { useEffect } from 'react';
import type { BlockDefinition, BlockProps } from '../../types';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken } from '@vibesites/runtime/react';
import { useTrackingAllowed } from '../../shared/analytics';

const SCRIPT_ID = 'basis-tracking-pixel-script';
const TRACKED_PAGES_KEY = '__vibesitesBasisTrackedPages';
const DATA_OBJECT_NAME = 'cntrData';

type BasisWindow = Window & {
  cntrUpTag?: {
    track?: (dataObjectName: string, universalPixelId: string) => void;
  };
  __vibesitesBasisScriptPromise?: Promise<void>;
  [TRACKED_PAGES_KEY]?: Set<string>;
  [key: string]: unknown;
};

function basisWindow(): BasisWindow {
  return window as unknown as BasisWindow;
}

function loadBasisScript(): Promise<void> {
  const win = basisWindow();
  if (win.__vibesitesBasisScriptPromise) return win.__vibesitesBasisScriptPromise;

  win.__vibesitesBasisScriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Basis script failed to load')), { once: true });
      if (win.cntrUpTag) resolve();
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = 'https://cdn01.basis.net/assets/up.js?um=1';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Basis script failed to load'));
    document.head.appendChild(script);
  });

  return win.__vibesitesBasisScriptPromise;
}

function getTrackedPages(): Set<string> {
  const win = basisWindow();
  if (!win[TRACKED_PAGES_KEY]) win[TRACKED_PAGES_KEY] = new Set();
  return win[TRACKED_PAGES_KEY]!;
}

function BasisTrackingPixelComponent(_props: BlockProps) {
  const universalPixelId = useBlockToken('universal_pixel_id', { defaultValue: '' })?.trim();
  const trackingAllowed = useTrackingAllowed('advertising');

  useEffect(() => {
    if (!trackingAllowed || !universalPixelId) return;

    const pageKey = `${window.location.pathname}|${universalPixelId}`;
    const trackedPages = getTrackedPages();
    if (trackedPages.has(pageKey)) return;
    trackedPages.add(pageKey);

    void loadBasisScript().then(() => {
      basisWindow().cntrUpTag?.track?.(DATA_OBJECT_NAME, universalPixelId);
    });
  }, [trackingAllowed, universalPixelId]);

  return null;
}

const contentSchema: ComponentContentSchema = {
  slot: 'BasisTrackingPixel',
  displayName: 'Basis Tracking Pixel',
  tokens: {
    universal_pixel_id: {
      label: 'Universal Pixel ID',
      type: 'text',
      description: 'Basis Universal Pixel ID from the cntrUpTag.track snippet.',
      required: true,
    },
  },
};

export const basisTrackingPixelBlock: BlockDefinition = {
  id: 'basis-tracking-pixel',
  name: 'Basis Universal Pixel',
  description:
    'Basis Universal Pixel tracking. Loads up.js and fires the universal pixel on page view.',
  category: 'Analytics',
  placement: 'injection',
  defaultScope: 'global',
  component: BasisTrackingPixelComponent,
  contentSchema,
  tags: ['basis', 'centro', 'sitescout', 'tracking', 'pixel', 'analytics'],
};
